import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useSeoHead, useGa4 } from "@/lib/seoHead";

const KEY = "cookie_consent";
const rid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);

function ids() {
  let v = localStorage.getItem("visitor_id");
  if (!v) { v = rid(); localStorage.setItem("visitor_id", v); }
  let s = sessionStorage.getItem("visit_session");
  if (!s) { s = rid(); sessionStorage.setItem("visit_session", s); }
  return { visitor_id: v, session_id: s };
}

function useVisitTracking(enabled) {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!enabled) return;
    const last = sessionStorage.getItem("visit_last");
    const key = `${pathname}|${Math.floor(Date.now() / 3000)}`;
    if (last === key) return;
    sessionStorage.setItem("visit_last", key);
    if (sessionStorage.getItem("visit_ref") === null) sessionStorage.setItem("visit_ref", document.referrer || "");
    api.post("/track", { path: pathname, referrer: sessionStorage.getItem("visit_ref"), ...ids() }).catch(() => {});
  }, [enabled, pathname]);
}

export default function CookieConsent() {
  const { settings } = useSettings();
  const [accepted, setAccepted] = useState(() => localStorage.getItem(KEY) === "accepted");
  useVisitTracking(accepted);
  useSeoHead();
  useGa4(settings?.ga4_measurement_id, accepted);
  if (accepted) return null;
  const accept = () => { localStorage.setItem(KEY, "accepted"); setAccepted(true); };
  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-5 pointer-events-none" data-testid="cookie-banner">
      <div className="pointer-events-auto mx-auto max-w-3xl rounded-2xl border border-white/10 bg-[#0d1117]/95 backdrop-blur-xl shadow-2xl p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 animate-in slide-in-from-bottom-4 duration-500">
        <Cookie className="hidden sm:block shrink-0 text-[hsl(var(--primary))]" size={28} />
        <p className="text-sm text-white/70 leading-relaxed">
          We use cookies to keep you signed in, remember your preferences, measure visits and show relevant ads.
          By continuing you agree to our <Link to="/privacy" className="text-[hsl(var(--primary))] hover:underline" data-testid="cookie-privacy-link">Privacy Policy</Link>.
        </p>
        <button onClick={accept} data-testid="cookie-accept-btn"
          className="shrink-0 px-6 py-2.5 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-bold text-sm hover:-translate-y-0.5 transition-transform">
          Accept cookies
        </button>
      </div>
    </div>
  );
}
